import { useContext, useEffect } from "react";
import RegistrationContext from "./contexts/RegistrationContext";
import setAuthToken from "./authtoken";
import NavBar from "./components/NavBar/NavBar";
import Footer from "./components/Footer/Footer";
import StatusBar from "./components/StatusBar/StatusBar";
import AdminMenu from "./components/AdminMenu/AdminMenu";
import axios from "axios";
import { base_url } from "./config";
import parseAnswer from "./util/parseAnswer";
import NotAuthenticated from "./sections/NotAuthenticated";
import RegularUser from "./sections/RegularUser";
import AdminUser from "./sections/AdminUser";
import RegistrationComplete from "./sections/RegistrationComplete";
import Loading from "./pages/Loading/Loading";
import Disclaimer from "./sections/Disclaimer";
import Header from "./components/Header/Header";
import HeaderLogged from "./components/HeaderLogged/HeaderLogged";
import Stepper from "./components/Stepper/Stepper";

function App() {
  const {
    screen,
    authenticated,
    setAuthenticated,
    reload,
    setScreen,
    setMaxStep,
    setData,
    setError,
    setStatus,
    setReload,
    setCart,
    setEmail,
    setPhase,
    setPaperSelected,
    setPaperSelectedVerified,
    setIsAdmin,
    setAdminRole,
    loading,
    setLoading,
    setIsStudent,
    setIsMainAccess,
    setOptionAccess,
    setRegistrationType,
    setRegistrationCode,
    setPrices,
    next,
    setNext,
    setIsImpersonating,
    setUpgradableOptions,
    setIsSoldout,
    isSoldout,
    disclaimerAccepted,
    setDisclaimerAccepted,
    setWorkshops,
    setTutorials,
    setPapers,
    setPermissions,
    setPpiTier,
  } = useContext(RegistrationContext);

  const token = localStorage.getItem("token");
  if (token) {
    setAuthenticated(true);
    setAuthToken(token);
  }

  useEffect(() => {
    setLoading(true);

    axios
      .get(`${base_url}/api/papers`)
      .then((response) => setPapers(response.data))
      .catch(() => {});

    axios
      .get(`${base_url}/api/progress`)
      .then((response) => {
        setScreen(parseAnswer(response));
        setMaxStep(response.data.max_step);
        setRegistrationCode(response.data.regcode);
        if (response && response.data.data) {
  
          setData(response.data.data);
          if (response.data.data.papers) {
            setPaperSelected(response.data.data.papers);
            setPaperSelectedVerified(response.data.data.registered_papers);
          } else {
            setPaperSelected("");
            setPaperSelectedVerified("");
          }
          if (response.data.data.workshops) {
            setWorkshops(response.data.data.workshops)
          }
          if (response.data.data.tutorials) {
            setTutorials(response.data.data.tutorials)
          }
        }
        if (response && response.data.cart) {
          setCart(response.data.cart);
        }
        setEmail(response.data.email);
        setPhase(response.data.phase);
        setIsImpersonating(response.data.impersonating);
        if (response && response.data.pr) {
          setPrices(JSON.parse(atob(response.data.pr)));
        }
        setIsStudent(response.data.type === "student");
        setIsMainAccess(response.data.option === "main");
        setOptionAccess(response.data.option);
        setRegistrationType(response.data.type);
        
        setLoading(false);
        setIsAdmin(['admin', 'viewer'].includes(response.data.role));
        setAdminRole(['admin', 'viewer'].includes(response.data.role) ? response.data.role : "N/A");
        setPermissions(response.data.permissions || []);
        setPpiTier(response.data.ppi_tier || null);

        setIsSoldout(response.data.sold_out);
        setUpgradableOptions([...response.data.upgradable_options]);
        setUpgradableOptions([...response.data.upgradable_options]);
        setDisclaimerAccepted(response.data.disclaimer_accepted);
      

        if (next) {
          setScreen(next);
          setNext(null);
        }
      })
      .catch(() => {
        setLoading(false);
        setAuthToken("");

        localStorage.removeItem("token");
        setScreen("signin");
        setData({});
        setError("");
        setStatus("");
        setMaxStep("");
        setAuthenticated(false)
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    reload,
    setScreen,
    setAuthenticated,
    setError,
    setStatus,
    setReload,
    setMaxStep,
    setData,
    setEmail,
    setCart,
    setIsAdmin,
    setPaperSelected,
    setPhase,
    setLoading,
    setIsMainAccess,
    setIsStudent,
    setOptionAccess,
    setPrices,
    setDisclaimerAccepted,
    setWorkshops,
    setTutorials
  ]);

  useEffect(() => {
    if (!authenticated) {
      async function fetchData() {
        try {
          const response = await axios.post(`${base_url}/api/get_phase`);
          setPhase(response.data.phase);
        } catch (error) {
          setError("Network error");
        }
      }
      fetchData();
    }
  }, [authenticated, setError, setPhase]);

  useEffect(() => setLoading(true), [setLoading]);

  const isAdminScreen = screen.startsWith("admin");

  if (loading) {
    return (<div className="App"><Loading /></div>);
  }
  if (!authenticated) return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-gray-50 to-gray-100 font-sans">
      {/* Header */}
      <Header />
      <StatusBar />
      <NotAuthenticated />
      <Footer />
    </div>
  );
  if (authenticated && !isAdminScreen) {
    return (
      <div className="h-screen flex  flex-col bg-gradient-to-b from-gray-50 to-gray-100 font-sans">
        {/* Header */}
        <HeaderLogged />
        <StatusBar />

        {(!disclaimerAccepted) ? (<Disclaimer />) : (<><Stepper /> <RegularUser /></>)}

        <Footer />
      </div>
    );
  }

  if (authenticated && isAdminScreen ) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-b from-gray-50 to-gray-100 font-sans">
        {/* Header */}
        <HeaderLogged />
        <AdminMenu />
        
        <AdminUser /> 
        <Footer />
      </div>
    );
  }
  return (
    <div className="App">
      {/* Main header with sign in, log out, etc */}
      
      <div className="nav">
        <NavBar boxed={!isAdminScreen} />
      </div>

      {/* To show messages like a snack bar */}
   
      
      {!authenticated && <NotAuthenticated />}

      {authenticated && !isAdminScreen && !disclaimerAccepted && <Disclaimer />}

      {authenticated && !isAdminScreen && disclaimerAccepted && screen !== "Confirmation" && !isSoldout && (
        <RegularUser />
      )}

      {authenticated && !isAdminScreen && disclaimerAccepted && screen === "Confirmation" && (
        <RegistrationComplete />
      )}
      {authenticated && isAdminScreen && <AdminUser />}

      <div>
      <div>
      
      </div>
        {!isAdminScreen && <Footer />}

      </div>
    </div>
  );
}

export default App;
